<template>
  <GradientBackground
    :gradientTop="styles.homeBgGradientTop || '#1a1a1a'"
    :gradientBottom="styles.homeBgGradientBottom || '#ffffff'"
    :textureVariant="styles.backgroundTexture || ''"
    :textureOpacity="0.6"
  >
    <!-- Header with Logo and Avatar Stack / Close Button -->
    <div class="relative z-10 flex justify-between px-2 items-center mb-8 pt-2">
      <!-- Company Logo and Name -->
      <div class="flex items-center">
        <!-- Company Logo -->
        <div class="w-10 h-10 rounded-lg overflow-hidden mr-2 flex-shrink-0">
          <img
            :src="styles.companyLogo || defaultLogo"
            alt="Logo"
            class="w-full h-full object-cover"
          />
        </div>
        <!-- Company Name -->
        <div
          class="font-bold text-xl font-sans"
          :style="{ color: styles.textColor || '#ffffff' }"
        >
          {{ styles.companyName }}
        </div>
      </div>

      <!-- Mobile: Show close button instead of avatars -->
      <div v-if="isMobile" class="flex items-center">
        <button
          @click="$emit('close-widget')"
          class="p-2 text-white rounded-full transition-all duration-300 border-none bg-transparent cursor-pointer hover:bg-white hover:bg-opacity-20"
        >
          <MaCross class="w-5 h-5" />
        </button>
      </div>

      <!-- Desktop: Show avatars as normal -->
      <div v-else-if="styles.showAvatars" class="flex items-center">
        <!-- Customer Avatar -->
        <div
          v-if="styles.customerAvatar"
          class="-ml-2 w-10 h-10 rounded-full overflow-hidden relative z-30"
          :style="{ 
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #737373'
          }"
        >
          <img
            :src="styles.customerAvatar"
            alt="Customer Avatar"
            class="w-full h-full object-cover"
          />
        </div>

        <!-- Fallback Avatar -->
        <div
          v-if="!styles.customerAvatar"
          class="-ml-2 w-10 h-10 rounded-full bg-emerald-500 overflow-hidden relative z-20"
          :style="{ 
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #737373'
          }"
        >
          <img
            src="../assets/ChatlyAvatar.png"
            alt="Default User"
            class="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>

    <!-- Welcome Section -->
    <div class="relative z-10 text-center mb-18 mt-6 px-6">
      <h2
        class="text-2xl font-semibold mb-2"
        :style="{ color: styles.textColor || '#ffffff' }"
      >
        {{ styles.welcomeText }}
      </h2>
      <p
        class="mt-1 text-sm"
        :style="{
          color: styles.textColor
            ? adjustColorOpacity(styles.textColor, 0.8)
            : '#e5e7eb',
        }"
      >
        {{ styles.welcomeSubText }}
      </p>
    </div>

    <!-- Content Area - White background -->
    <div class="relative z-10 bg-white rounded-t-lg p-6 flex-1 flex flex-col space-y-4">
      <!-- Search/Ask Card -->
      <div
        @click="navigateToChat"
        class="cursor-pointer bg-white rounded-lg p-3 -mt-12"
        :style="{ border: '1px solid #d1d5db' }"
      >
        <div class="text-sm flex text-gray-800 items-center justify-between">
          Sende uns eine Nachricht
          <button
            class="ml-2 px-1 rounded-lg text-white cursor-pointer border-none"
            :style="{
              backgroundColor: styles.buttonColor || styles.bubbleColor || '#1a1a1a',
            }"
          >
            <BsArrowRightShort class="w-6 h-6" />
          </button>
        </div>
      </div>

      <!-- Other Components -->
      <HighlightBanner
        v-if="styles.showBanner"
        :banner="getBannerData()"
        :buttonColor="styles.buttonColor || styles.bubbleColor"
        :aspectRatio="styles.aspectRatio || 'rectangle'"
      />

      <TextSuggestions
        v-if="styles.showTextSuggestions"
        :options="getSupportTopics()"
        :title="styles.supportTopicsTitle"
        @select-option="handleOptionSelect"
        @navigate-to-faq="handleFaqNavigation"
      />

      <MoodBar
        v-if="styles.showMoodBar"
        @mood-selected="handleMoodSelection"
        @mood-reset="handleMoodReset"
      />

      <Callback
        v-if="styles.showCallback"
        :styles="styles"
        @callback-requested="handleCallbackRequest"
      />

      <ChatChannels
        v-if="styles.showChatChannels"
        :channels="getContactChannels()"
        :sectionTitle="styles.channelsSectionTitle"
      />

      <ContactForm
        v-if="styles.showContactForm"
        :styles="styles"
        @form-submitted="handleFormSubmit"
      />

      <!-- Bottom spacing -->
      <div class="h-4"></div>
    </div>
  </GradientBackground>
</template>

<script>
import GradientBackground from "../components/GradientBackground.vue";
import TextSuggestions from "../widgets/TextSuggestions.vue";
import MoodBar from "../widgets/MoodBar.vue";
import ChatChannels from "../widgets/ChatChannels.vue";
import ContactForm from "../widgets/ContactForm.vue";
import HighlightBanner from "../widgets/HighlightBanner.vue";
import Callback from "../widgets/Callback.vue";
import { Client, Databases, Query } from "appwrite";
import { appwriteConfig } from "../lib/appwrite";
import catLogo from "../assets/catLogo.png";
import { MaCross, BsArrowRightShort } from "@kalimahapps/vue-icons";

export default {
  name: "HomeScreen",
  components: {
    GradientBackground,
    TextSuggestions,
    MoodBar,
    ChatChannels,
    ContactForm,
    HighlightBanner,
    Callback,
    MaCross,
    BsArrowRightShort,
  },
  props: {
    styles: {
      type: Object,
      default: () => ({
        bubbleColor: "#1a1a1a",
        bubbleHoverColor: "#1a1a1a",
        homeBgGradientTop: "#1a1a1a",
        homeBgGradientBottom: "#ffffff",
        showAvatars: true,
        textColor: "#ffffff",
        customerAvatar: null,
        buttonColor: null,
      }),
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "form-submitted",
    "callback-requested",
    "change-screen",
    "close-widget",
    "navigate-to-faq",
  ],
  data() {
    return {
      appwriteClient: null,
      databases: null,
      currentMood: null,
      moodRatings: [],
      companyDocumentId: null,
      userAvatar: null,
      defaultLogo: catLogo,
    };
  },
  created() {
    this.initAppwrite();
  },
  methods: {
    // Initialize Appwrite client and fetch user data
    async initAppwrite() {
      try {
        this.appwriteClient = new Client();
        this.appwriteClient
          .setEndpoint(appwriteConfig.endpoint)
          .setProject(appwriteConfig.projectId);

        this.databases = new Databases(this.appwriteClient);

        const companyId = this.styles.companyID;

        if (!companyId) {
          console.log("No company ID provided - using default styles only");
          return;
        }

        console.log("Using company ID:", companyId);

        const response = await this.databases.listDocuments(
          appwriteConfig.databaseId,
          appwriteConfig.messagerstyleId,
          [Query.equal("companyID", companyId)]
        );

        if (response.documents && response.documents.length > 0) {
          this.companyDocumentId = response.documents[0].$id;
          console.log("Company document found:", this.companyDocumentId);
        } else {
          console.log("No company document found for ID:", companyId);
        }

        await this.fetchUserAvatar(companyId);
      } catch (error) {
        console.error("Error initializing Appwrite:", error);
      }
    },

    // Fetch user avatar from userCollection
    async fetchUserAvatar(companyId) {
      if (!companyId) {
        console.log("No company ID provided for avatar fetch");
        return;
      }

      try {
        console.log("Fetching user avatar for companyId:", companyId);

        const userResponse = await this.databases.listDocuments(
          appwriteConfig.databaseId,
          appwriteConfig.userCollectionId,
          [Query.equal("companyId", companyId)]
        );

        console.log("User response:", userResponse);

        if (userResponse.documents && userResponse.documents.length > 0) {
          this.userAvatar = userResponse.documents[0].avatar || null;
          console.log("User avatar fetched:", this.userAvatar);
        } else {
          const alternateResponse = await this.databases.listDocuments(
            appwriteConfig.databaseId,
            appwriteConfig.userCollectionId,
            [Query.equal("companyID", companyId)]
          );

          if (
            alternateResponse.documents &&
            alternateResponse.documents.length > 0
          ) {
            this.userAvatar = alternateResponse.documents[0].avatar || null;
            console.log(
              "User avatar fetched (alternate field):",
              this.userAvatar
            );
          } else {
            console.log("No user found for company ID:", companyId);
          }
        }
      } catch (error) {
        console.error("Error fetching user avatar:", error);
      }
    },

    // Utility function to adjust color opacity
    adjustColorOpacity(color, opacity) {
      // Convert hex to rgba with specified opacity
      if (color.startsWith("#")) {
        const hex = color.replace("#", "");
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      }
      return color; // Return original if not hex
    },

    async handleMoodSelection(moodData) {
      if (!this.styles.companyID) {
        console.log("No company ID - mood data not saved to database");
        return;
      }

      try {
        if (!this.companyDocumentId) {
          const companyId = this.styles.companyID;
          const response = await this.databases.listDocuments(
            appwriteConfig.databaseId,
            appwriteConfig.messagerstyleId,
            [Query.equal("companyID", companyId)]
          );

          if (response.documents && response.documents.length > 0) {
            this.companyDocumentId = response.documents[0].$id;
          } else {
            console.error("Company document not found");
            return;
          }
        }

        const timestamp = new Date().toISOString();

        const moodEntryString = JSON.stringify({
          value: moodData.textValue,
          numeric: moodData.numericValue,
          label: moodData.label,
          timestamp: timestamp,
        });

        const currentDoc = await this.databases.getDocument(
          appwriteConfig.databaseId,
          appwriteConfig.messagerstyleId,
          this.companyDocumentId
        );

        let moodRatingsArray = [];
        if (currentDoc.moodRatings && Array.isArray(currentDoc.moodRatings)) {
          moodRatingsArray = [...currentDoc.moodRatings];
        }

        moodRatingsArray.push(moodEntryString);

        if (moodRatingsArray.length > 50) {
          moodRatingsArray = moodRatingsArray.slice(-50);
        }

        let averageMood = 0;
        if (moodRatingsArray.length > 0) {
          const sum = moodRatingsArray.reduce((total, ratingStr) => {
            try {
              const rating = JSON.parse(ratingStr);
              return total + rating.numeric;
            } catch (e) {
              return total;
            }
          }, 0);
          averageMood = sum / moodRatingsArray.length;
        }

        await this.databases.updateDocument(
          appwriteConfig.databaseId,
          appwriteConfig.messagerstyleId,
          this.companyDocumentId,
          {
            moodRatings: moodRatingsArray,
            moodRatingAverage: averageMood,
            moodRatingCount: moodRatingsArray.length,
            lastMoodRating: moodData.numericValue,
            lasMoodValue: moodData.textValue,
          }
        );

        console.log("Mood rating saved successfully");
      } catch (error) {
        console.error("Error saving mood rating:", error);
      }
    },

    getBannerData() {
      return {
        imageUrl:
          this.styles.bannerImage ||
          "https://images.unsplash.com/photo-1607083206869-4c7672e72a8a",
        title: this.styles.bannerTitle || "Black Friday Sale!",
        subtitle:
          this.styles.bannerSubTitle ||
          "Sichere dir 30% Rabatt auf alle Produkte",
        buttonUrl: this.styles.bannerLink || "",
        buttonText: this.styles.bannerButton || "",
      };
    },

    getContactChannels() {
      const channels = [];

      // Instagram
      if (
        this.styles.instagramLink &&
        this.styles.instagramLink.trim() !== "" &&
        this.styles.instagramLink !== "yourcompany"
      ) {
        const instagramHandle = this.styles.instagramLink
          .trim()
          .replace("@", "");
        channels.push({
          name: "Instagram",
          url: `https://instagram.com/${instagramHandle}`,
          color: "from-purple-500 via-pink-500 to-yellow-500",
          type: "instagram",
        });
      }

      // WhatsApp
      if (
        this.styles.whatsappNumber &&
        this.styles.whatsappNumber.trim() !== "" &&
        this.styles.whatsappNumber !== "yourphonenumber"
      ) {
        const phoneNumber = this.styles.whatsappNumber
          .trim()
          .replace(/[\s\-\(\)]/g, "");
        channels.push({
          name: "WhatsApp",
          url: `https://wa.me/${phoneNumber}`,
          color: "bg-green-500",
          type: "whatsapp",
        });
      }

      // Email
      if (
        this.styles.emailAddress &&
        this.styles.emailAddress.trim() !== "" &&
        this.styles.emailAddress !== "contact@yourcompany.com"
      ) {
        channels.push({
          name: "E-Mail",
          url: `mailto:${this.styles.emailAddress.trim()}`,
          color: "bg-blue-500",
          type: "email",
        });
      }

      // Phone
      if (
        this.styles.phoneNumber &&
        this.styles.phoneNumber.trim() !== "" &&
        this.styles.phoneNumber !== "+49123456789"
      ) {
        channels.push({
          name: "Telefon",
          url: `tel:${this.styles.phoneNumber.trim()}`,
          color: "bg-yellow-500",
          type: "phone",
        });
      }

      return channels;
    },

    getSupportTopics() {
      const topics = [];

      if (this.styles.supportTopic1) {
        topics.push({
          text: this.styles.supportTopic1,
          target: this.styles.supportTopic1Target || "ChatScreen",
          faqId: this.styles.supportTopic1Id || null,
        });
      }

      if (this.styles.supportTopic2) {
        topics.push({
          text: this.styles.supportTopic2,
          target: this.styles.supportTopic2Target || "TerminScreen",
          faqId: this.styles.supportTopic2Id || null,
        });
      }

      if (this.styles.supportTopic3) {
        topics.push({
          text: this.styles.supportTopic3,
          target: this.styles.supportTopic3Target || "ChatScreen",
          faqId: this.styles.supportTopic3Id || null,
        });
      }

      if (this.styles.supportTopic4) {
        topics.push({
          text: this.styles.supportTopic4,
          target: this.styles.supportTopic4Target || "FaqScreen",
          faqId: this.styles.supportTopic4Id || null,
        });
      }

      return topics;
    },

    navigateToChat() {
      this.$emit("change-screen", "ChatScreen");
      console.log("Navigating to chat screen");
    },

    handleOptionSelect(option) {
      if (option.target === "ChatScreen") {
        this.$emit("change-screen", {
          screen: "ChatScreen",
          preselectedMessage: option.text,
        });
      } else {
        this.$emit("change-screen", option.target);
      }
    },

    handleFaqNavigation(faqEvent) {
      this.$emit("navigate-to-faq", faqEvent);
    },

    handleMoodReset() {
      this.currentMood = null;
    },

    handleFormSubmit(formData) {
      this.$emit("form-submitted", formData);
    },

    handleCallbackRequest(callbackData) {
      this.$emit("callback-requested", callbackData);
    },
  },
};
</script>